import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ExternalLink, Scale, Loader2, AlertCircle, Copy, Check, ArrowLeft, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { getCard } from "@/lib/api";

export default function CardLandingPage({ dark, toggleTheme }) {
  const { cardId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getCard(cardId).then(setData).catch(() => setError(true));
  }, [cardId]);

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* ignore */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Link kopiert.");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <>
      <Header dark={dark} toggleTheme={toggleTheme} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {error ? (
          <div className="text-center py-20">
            <AlertCircle className="h-10 w-10 mx-auto text-slate-400" />
            <p className="mt-4 text-slate-600 dark:text-slate-300">Diese Karte wurde nicht gefunden.</p>
            <Link to="/" className="mt-4 inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium">
              <ArrowLeft className="h-4 w-4" /> Zum Studio
            </Link>
          </div>
        ) : !data ? (
          <div className="grid place-items-center py-24 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
                Karte {data.card.number} · {data.index + 1}/{data.total}
              </span>
              <button
                data-testid="copy-juris-link-button"
                onClick={copyLink}
                className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
              >
                {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                Link teilen
              </button>
            </div>

            <h1
              data-testid="juris-landing-card-title"
              className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 leading-tight"
            >
              {data.card.title}
            </h1>

            <div className="mt-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Frage</div>
              <p data-testid="juris-landing-card-question" className="mt-2 text-lg text-slate-800 dark:text-slate-100 leading-relaxed">
                {data.card.question}
              </p>
              <div className="mt-6 text-xs font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Antwort</div>
              <p data-testid="juris-landing-card-answer" className="mt-2 text-slate-700 dark:text-slate-300 leading-relaxed">
                {data.card.answer}
              </p>
            </div>

            <div className="mt-8">
              <div className="flex items-center gap-2 mb-3">
                <Scale className="h-5 w-5 text-[#0F2942] dark:text-blue-400" />
                <h2 className="font-display text-xl font-bold text-slate-900 dark:text-slate-100">
                  Rechtsquellen im Jurisportal
                </h2>
              </div>
              {data.card.sources?.length ? (
                <div className="space-y-3">
                  {data.card.sources.map((s, i) => (
                    <a
                      key={s.number}
                      data-testid={`juris-source-link-${i}`}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-start gap-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-sm transition-all"
                    >
                      <span className="shrink-0 grid place-items-center h-9 w-9 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 font-mono text-sm font-bold">
                        {s.number}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 dark:text-slate-100 leading-snug">
                          {s.court || s.description}
                          {s.aktenzeichen && (
                            <span className="ml-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                              {s.aktenzeichen}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-sm text-slate-500 dark:text-slate-400 line-clamp-2">
                          {[s.date, s.type, s.description].filter(Boolean).join(" · ")}
                        </div>
                        {s.norms && (
                          <div className="mt-1 text-xs font-mono text-slate-400 dark:text-slate-500 truncate">
                            {s.norms}
                          </div>
                        )}
                      </div>
                      <ExternalLink className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-blue-500 mt-1" />
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 dark:text-slate-400 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" /> Für diese Karte sind keine Quellen hinterlegt.
                </p>
              )}
            </div>

            <p className="mt-6 text-xs text-slate-400 dark:text-slate-500">
              Hinweis: Der Aufruf im Jurisportal erfordert ggf. eine Anmeldung/Lizenz.
            </p>
          </div>
        )}
      </main>
    </>
  );
}
